// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { workspaceAPI, courseAPI } from "@/lib/api";
import api from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BookOpen, 
  FileText, 
  Award,
  TrendingUp,
  CheckCircle2,
  Clock,
  User,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { getInitials } from "@/lib/utils";

export default function ProgressPage() {
  const { workspaceId } = useParams();
  const { user, canManage } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myProgress, setMyProgress] = useState(null);
  const [allProgress, setAllProgress] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseProgress, setCourseProgress] = useState({});

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    try {
      const [progressRes, coursesRes] = await Promise.all([
        api.get(`/workspaces/${workspaceId}/progress`),
        courseAPI.list(workspaceId),
      ]);
      
      setMyProgress(progressRes.data);
      setCourses(coursesRes.data);

      // Load individual course progress
      const progressMap = {};
      for (const course of coursesRes.data) {
        try {
          const res = await api.get(`/workspaces/${workspaceId}/courses/${course.id}/progress`);
          progressMap[course.id] = res.data;
        } catch (e) {
          progressMap[course.id] = { completed: false, progress_percent: 0 };
        }
      }
      setCourseProgress(progressMap);

      // Load all participants progress if trainer
      if (canManage) {
        try {
          const allRes = await api.get(`/workspaces/${workspaceId}/progress/all`);
          setAllProgress(allRes.data);
        } catch (e) {
          console.error("Failed to load all progress:", e);
        }
      }
    } catch (error) {
      console.error("Failed to load progress:", error);
      toast.error("Erreur lors du chargement de la progression");
    } finally {
      setLoading(false);
    }
  };

  const markCourseComplete = async (courseId) => {
    try {
      await api.put(`/workspaces/${workspaceId}/courses/${courseId}/progress`, {
        course_id: courseId,
        completed: true,
        progress_percent: 100,
      });
      setCourseProgress({
        ...courseProgress,
        [courseId]: { ...courseProgress[courseId], completed: true, progress_percent: 100 }
      });
      toast.success("Cours marqué comme terminé!");
      loadData();
    } catch (error) {
      console.error("Failed to mark complete:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight">Progression</h1>
          <p className="mt-1 text-muted-foreground">
            {canManage ? "Suivez la progression de tous les participants" : "Suivez votre progression dans cette formation"}
          </p>
        </div>

        <Tabs defaultValue="my-progress">
          <TabsList className="mb-6">
            <TabsTrigger value="my-progress">Ma progression</TabsTrigger>
            {canManage && <TabsTrigger value="all-progress">Tous les participants</TabsTrigger>}
          </TabsList>

          <TabsContent value="my-progress" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <BookOpen className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold">
                        {myProgress?.completed_courses || 0}/{myProgress?.total_courses || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Cours terminés</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10">
                      <TrendingUp className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold">
                        {Math.round(myProgress?.overall_progress || 0)}%
                      </p>
                      <p className="text-sm text-muted-foreground">Progression globale</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10">
                      <FileText className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold">
                        {myProgress?.submitted_assignments || 0}/{myProgress?.total_assignments || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Devoirs soumis</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-500/10">
                      <Award className="h-6 w-6 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold">
                        {myProgress?.average_score ? Math.round(myProgress.average_score) : "-"}
                      </p>
                      <p className="text-sm text-muted-foreground">Note moyenne</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Overall Progress */}
            <Card>
              <CardHeader>
                <CardTitle>Progression globale</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Cours terminés</span>
                    <span className="font-medium">{Math.round(myProgress?.overall_progress || 0)}%</span>
                  </div>
                  <Progress value={myProgress?.overall_progress || 0} className="h-3" />
                </div>
              </CardContent>
            </Card>

            {/* Course Progress */}
            <Card>
              <CardHeader>
                <CardTitle>Progression par cours</CardTitle>
                <CardDescription>Cliquez sur un cours pour le marquer comme terminé</CardDescription>
              </CardHeader>
              <CardContent>
                {courses.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Aucun cours dans cet espace
                  </p>
                ) : (
                  <div className="space-y-4">
                    {courses.map((course) => {
                      const progress = courseProgress[course.id] || {};
                      return (
                        <div 
                          key={course.id}
                          className="flex items-center gap-4 p-4 rounded-lg border hover:border-primary/50 transition-colors cursor-pointer"
                          onClick={() => !progress.completed && markCourseComplete(course.id)}
                        >
                          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                            progress.completed ? 'bg-green-500/10' : 'bg-muted'
                          }`}>
                            {progress.completed ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            ) : (
                              <BookOpen className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Link 
                                to={`/workspace/${workspaceId}/courses/${course.id}`}
                                className="font-medium hover:text-primary"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {course.title}
                              </Link>
                              {progress.completed && (
                                <Badge className="bg-green-500 text-white">Terminé</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {course.chapter || "Sans chapitre"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{progress.progress_percent || 0}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {canManage && (
            <TabsContent value="all-progress" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Progression des participants</CardTitle>
                  <CardDescription>{allProgress.length} participant(s)</CardDescription>
                </CardHeader>
                <CardContent>
                  {allProgress.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Aucun participant dans cet espace
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {allProgress.map((item, index) => (
                        <div 
                          key={item.user.id}
                          className="flex items-center gap-4 p-4 rounded-lg border"
                        >
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getInitials(item.user.first_name, item.user.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">
                              {item.user.first_name} {item.user.last_name}
                            </p>
                            <p className="text-sm text-muted-foreground">{item.user.email}</p>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-lg font-semibold">{Math.round(item.course_progress)}%</p>
                              <p className="text-xs text-muted-foreground">Cours</p>
                            </div>
                            <div>
                              <p className="text-lg font-semibold">
                                {item.submitted_assignments}/{item.total_assignments}
                              </p>
                              <p className="text-xs text-muted-foreground">Devoirs</p>
                            </div>
                            <div>
                              <p className="text-lg font-semibold">
                                {item.average_score ? Math.round(item.average_score) : "-"}
                              </p>
                              <p className="text-xs text-muted-foreground">Moyenne</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </WorkspaceLayout>
  );
}
